import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { initTheme, persistTheme, themeForMode } from '../lib/themeController';

export const THEMES = {
  dark: {
    bg0: '#0d1117', bg1: '#11161d', bg2: '#161b22', bg3: '#21262d', bg4: '#30363d',
    border: 'rgba(255,255,255,0.07)', borderHover: 'rgba(255,255,255,0.13)',
    text0: '#e6edf3', text1: '#c9d1d9', text2: '#8b949e', text3: '#6e7681', text4: '#484f58',
    cyan: '#00d4ff', emerald: '#34d399',
    accent: '#00d4ff', accentBg: 'rgba(0,212,255,0.12)', accentBorder: 'rgba(0,212,255,0.45)',
    link: '#58a6ff', warnText: '#fbbf24', amber: '#fbbf24', red: '#f87171', purple: '#a78bfa', okText: '#34d399',
    aiBanner: { bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.45)', text: '#c4b5fd' },
    pillBg: 'rgba(13,17,23,0.85)', pillText: '#e6edf3',
  },
  light: {
    bg0: '#f8fafc', bg1: '#ffffff', bg2: '#e6edf3', bg3: '#e2e8f0', bg4: '#c9d1d9',
    border: 'rgba(0,0,0,0.08)', borderHover: 'rgba(0,0,0,0.15)',
    text0: '#11161d', text1: '#161b22', text2: '#484f58', text3: '#6e7681', text4: '#8b949e',
    cyan: '#0e7490', emerald: '#047857',
    accent: '#0e7490', accentBg: 'rgba(14,116,144,0.10)', accentBorder: 'rgba(14,116,144,0.55)',
    link: '#1d4ed8', warnText: '#92400e', amber: '#92400e', red: '#dc2626', purple: '#6d28d9', okText: '#047857',
    aiBanner: { bg: '#ede9fe', border: '#a78bfa', text: '#4c1d95' },
    pillBg: 'rgba(255,255,255,0.92)', pillText: '#11161d',
  },
};

const ThemeContext = createContext({
  theme: 'dark',
  mode: 'system',
  setMode: () => {},
  setTheme: () => {},
  toggleTheme: () => {},
  C: THEMES.dark,
});

export function ThemeProvider({ children }) {
  const [init] = useState(() => initTheme());
  const [mode, setModeState] = useState(init.mode || init.theme);
  const [systemLight, setSystemLight] = useState(() => !!window.matchMedia?.('(prefers-color-scheme: light)')?.matches);
  const theme = themeForMode(mode, systemLight);

  const themeRef = useRef(theme);
  themeRef.current = theme;

  // Follow the OS setting while the mode is 'system'.
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mq || mode !== 'system') return undefined;
    const onChange = (e) => setSystemLight(e.matches);
    setSystemLight(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [mode]);

  // Mirror the theme on <html> as both a class (legacy) and data-theme (CSS tokens).
  // Only explicit choices (toggle / setMode / setTheme) are persisted, never system-derived ones.
  useEffect(() => {
    document.documentElement.className = theme;
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // 'light' | 'dark' | 'system' (Settings > Preferences > Theme).
  const setMode = useCallback((next) => {
    persistTheme(next);
    setModeState(next);
  }, []);

  const setTheme = useCallback((next) => {
    const value = typeof next === 'function' ? next(themeRef.current) : next;
    setMode(value);
  }, [setMode]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, [setTheme]);

  const C = THEMES[theme] || THEMES.dark;

  const value = useMemo(() => ({
    theme,
    mode,
    setMode,
    setTheme,
    toggleTheme,
    C,
  }), [theme, mode, setMode, setTheme, toggleTheme, C]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

export default ThemeContext;
